<template>
  <div v-if="status && status.inventoryAlerts" class="alert alert-light border mb-3">
    <div v-if="!pushSupported">
      Push alerts need iOS 16.4+ or a desktop browser with notifications.
    </div>
    <div v-else-if="!standalone">
      <strong>Inventory alerts</strong>
      <p class="mb-1">
        On iPhone: open this site in Safari, tap Share, then
        <strong>Add to Home Screen</strong>. Open that icon and come back here to enable alerts.
      </p>
    </div>
    <div v-else class="d-flex flex-wrap align-items-center gap-2">
      <span v-if="status.subscribed" class="me-auto">Inventory alerts are on for this device.</span>
      <span v-else class="me-auto">Enable lock-screen alerts for supplies that need action.</span>
      <button
        type="button"
        class="btn btn-sm"
        :class="status.subscribed ? 'btn-outline-secondary' : 'btn-outline-primary'"
        :disabled="busy"
        @click="status.subscribed ? disableAlerts() : enableAlerts()"
      >
        {{ busy ? 'Please wait…' : (status.subscribed ? 'Disable alerts' : 'Enable alerts') }}
      </button>
    </div>
    <div v-if="error" class="text-danger small mt-2">{{ error }}</div>
  </div>
</template>

<script>
function isStandalone() {
  return window.matchMedia('(display-mode: standalone)').matches
    || window.navigator.standalone === true;
}

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; i += 1) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export default {
  name: 'PushAlerts',
  data: () => ({
    status: null,
    standalone: false,
    pushSupported: false,
    busy: false,
    error: '',
  }),
  async mounted() {
    this.standalone = isStandalone();
    this.pushSupported = 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
    await this.refreshStatus();
  },
  methods: {
    async refreshStatus() {
      try {
        const { data } = await this.$api.get('/push/status', { timeout: 10000 });
        this.status = data;
      } catch (error) {
        console.error('Error loading push status:', error);
      }
    },
    async enableAlerts() {
      this.busy = true;
      this.error = '';
      try {
        const permission = await Notification.requestPermission();
        if (permission !== 'granted') {
          this.error = 'Notifications were not allowed.';
          return;
        }

        const registration = await navigator.serviceWorker.ready;
        const { data: vapid } = await this.$api.get('/push/vapid-public-key', { timeout: 10000 });
        const subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(vapid.publicKey),
        });
        const json = subscription.toJSON();
        await this.$api.post('/push/subscriptions', {
          endpoint: json.endpoint,
          keys: json.keys,
          userAgent: navigator.userAgent,
        }, { timeout: 10000 });
        await this.refreshStatus();
      } catch (error) {
        console.error('Error enabling alerts:', error);
        this.error = error.response?.data?.error?.message || 'Could not enable alerts.';
      } finally {
        this.busy = false;
      }
    },
    async disableAlerts() {
      this.busy = true;
      this.error = '';
      try {
        const registration = await navigator.serviceWorker.ready;
        const subscription = await registration.pushManager.getSubscription();
        if (subscription) {
          await this.$api.delete('/push/subscriptions', {
            data: { endpoint: subscription.endpoint },
          });
          await subscription.unsubscribe();
        }
        await this.refreshStatus();
      } catch (error) {
        console.error('Error disabling alerts:', error);
        this.error = error.response?.data?.error?.message || 'Could not disable alerts.';
      } finally {
        this.busy = false;
      }
    },
  },
}
</script>
